import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { UpdateUserPreferencesV2CommandInput } from '@amzn/gbsa-orbit-typescript-client';

import { useUpdateUserPreferencesMutation } from '../data/user-preferences';
import { useCurrentUser, type UserPreferences } from './UserProvider';

/**
 * Query-string key that carries the URL's business unit id. Callers who
 * BUILD detail URLs (via `buildAuditDetailPath` / `buildTaskDetailPath` /
 * `buildPerformAuditPath` in `shared/routing`) and callers who READ them
 * agree on this exact name — do not inline the string on either side.
 */
export const URL_BUSINESS_UNIT_ID_PARAM = 'businessUnitId';

/**
 * Reconcile status:
 * - `match`: URL BU matches context, OR URL has no BU (legacy bookmark).
 *   Safe to render the page normally.
 * - `reconciling`: URL BU differs from context; the preferences mutation
 *   has been dispatched. Callers should keep the loading skeleton visible
 *   and suppress the "load failed" error banner — the underlying queries
 *   are about to refetch under the new BU.
 * - `error`: The reconcile mutation failed. Context BU was NOT switched;
 *   the detail page will fail to load its entity. Callers may surface a
 *   dedicated error banner suggesting a manual BU switch.
 */
export type ReconcileBusinessUnitStatus = 'match' | 'reconciling' | 'error';

export interface UseReconcileBusinessUnitFromUrlResult {
  readonly status: ReconcileBusinessUnitStatus;
  /** Populated only when `status === 'error'`. */
  readonly error: Error | undefined;
}

/**
 * Reconciles the URL's `businessUnitId` search param with the user's
 * currently-selected business unit (from `useCurrentUser`).
 *
 * When the two differ, this hook dispatches an
 * `UpdateUserPreferencesV2Command` — the SAME mutation the profile
 * preferences page fires when the user picks a BU from the dropdown.
 * Its `onSuccess` invalidates `sharedUserPreferencesKeys`, `UserProvider`
 * re-renders with the new BU, and every BU-scoped query (audit summary,
 * task detail, question responses, etc.) refetches on its own because
 * `businessUnitId` is part of their query keys. No app-wide reload.
 *
 * When they match, this hook is a no-op. When the URL has no
 * `businessUnitId` at all (older bookmarks predating this change), the
 * hook is a no-op — the page just loads against whatever BU the user
 * currently has selected, preserving legacy behavior.
 *
 * Loop protection: an in-hook ref tracks the URL BU we most recently
 * dispatched for. Once dispatched, we do NOT re-dispatch for the same
 * target — even while the mutation is in flight and even while context
 * still shows the old BU. A new URL BU (e.g. the user navigates from
 * one BU's audit to a different BU's audit) resets that guard and
 * re-dispatches. Once context catches up and matches the URL, the guard
 * is cleared so a subsequent divergence starts fresh.
 *
 * Recovery: on mutation error we DO NOT auto-retry — the caller sees
 * `status: 'error'` and can render a recovery UI. The user can always
 * switch BU manually via the profile-preferences page.
 *
 * Concurrency limitation: if two rapid BU-switch dispatches (e.g. the
 * user opens URL for BU=B then URL for BU=C in <500ms) hit the server
 * and the server processes them out of dispatch order, the final
 * userPreferences refetch may reflect the OLDER dispatch and context
 * settles on B while URL says C. This hook does not detect that race
 * and does not auto-retry — the ref shows we've already dispatched for
 * C, and there's no clean signal to distinguish "mutation still landing"
 * from "mutation landed but produced the wrong final context". Recovery
 * requires the next user interaction (refresh, navigate to another
 * URL) which triggers a fresh reconcile cycle. This is a documented
 * trade-off; the fix (mutation-completion tracking + conditional retry)
 * adds meaningful complexity for a scenario that self-heals on any
 * subsequent action.
 */
export function useReconcileBusinessUnitFromUrl(): UseReconcileBusinessUnitFromUrlResult {
  const [searchParams] = useSearchParams();
  const urlBusinessUnitId = searchParams.get(URL_BUSINESS_UNIT_ID_PARAM) ?? '';
  const { businessUnit, userPreferences, alias, programId } = useCurrentUser();

  const mutation = useUpdateUserPreferencesMutation({ programId, userId: alias });
  // React-query's `mutate` is documented as stable across renders — pull
  // it out of the mutation object so a change to `isPending` / `isError`
  // does not trip the effect below.
  const { mutate, isError, error } = mutation;

  // Tracks the URL BU we most recently dispatched a mutation for.
  // Prevents the effect from re-firing during the round-trip, and after
  // the round-trip but before the invalidation cascade has re-rendered
  // the tree — both would produce duplicate mutations.
  const lastDispatchedTargetRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Absent URL BU (legacy bookmark) → nothing to reconcile.
    if (!urlBusinessUnitId) return;
    // First-time user with no BU set — the profile preferences page
    // handles that flow; nothing to reconcile against yet.
    if (!businessUnit) return;
    // URL matches context — clear the guard so a FUTURE divergence
    // (e.g. navigating to a different BU's audit) can dispatch again.
    if (urlBusinessUnitId === businessUnit) {
      lastDispatchedTargetRef.current = undefined;
      return;
    }
    // Already dispatched for this URL BU — wait for the cascade rather
    // than firing again.
    if (lastDispatchedTargetRef.current === urlBusinessUnitId) return;

    lastDispatchedTargetRef.current = urlBusinessUnitId;
    mutate(buildBuSwitchInput(userPreferences, alias, programId, urlBusinessUnitId));
  }, [urlBusinessUnitId, businessUnit, userPreferences, alias, programId, mutate]);

  return useMemo<UseReconcileBusinessUnitFromUrlResult>(() => {
    // No reconcile work possible → report 'match' so callers render
    // normally. Three paths lead here:
    //   1. URL has no businessUnitId (legacy bookmark).
    //   2. User has no context BU yet — RequireProfileSetup will redirect
    //      them to /preferences before they see a detail page anyway.
    //   3. URL BU already matches context BU.
    if (!urlBusinessUnitId || !businessUnit || urlBusinessUnitId === businessUnit) {
      return { status: 'match', error: undefined };
    }
    if (isError) {
      return { status: 'error', error: error ?? undefined };
    }
    return { status: 'reconciling', error: undefined };
  }, [urlBusinessUnitId, businessUnit, isError, error]);
}

/**
 * Build the mutation payload for a URL-driven BU switch.
 *
 * Copies every non-BU field from the current preferences record so this
 * write preserves the user's saved locations, language, and notification
 * settings verbatim. The reconcile hook fires this on page load — the
 * user did NOT intend to touch those other fields, so we must not
 * overwrite them.
 *
 * Note: `buildBusinessUnitPreferences` (used by the profile-preferences
 * page's "save the current form" flow) is intentionally NOT called here.
 * That helper rewrites the location list for the current BU based on the
 * form's current state — meaningful when the user is editing locations,
 * meaningless during a URL-driven BU swap where locations are not being
 * touched.
 */
function buildBuSwitchInput(
  preferences: UserPreferences,
  alias: string,
  programId: string,
  targetBusinessUnitId: string,
): UpdateUserPreferencesV2CommandInput {
  return {
    programId,
    userId: alias,
    lastUpdatedBy: alias,
    profileSettings: {
      businessUnit: targetBusinessUnitId,
      businessUnitPreferences: preferences.profileSettings?.businessUnitPreferences ?? [],
      language: preferences.profileSettings?.language,
    },
    notificationSettings: {
      alertMeAbout: {
        task: preferences.notificationSettings?.alertMeAbout?.task,
        audit: preferences.notificationSettings?.alertMeAbout?.audit,
      },
      alertCenter: {
        email: preferences.notificationSettings?.alertCenter?.email,
      },
    },
  };
}
